// 配色スキーム（純関数・DuckDB/ブラウザ非依存）。
//
// 役割: コロプレス用の名前付きカラーランプ（アンカー色）を保持し、任意のクラス数 n に
//       サンプリングして RGB 配列を返す。連続/発散はアンカー間を線形補間、カテゴリは循環。
// 関係: viz/choropleth.js が buildChoroplethStyle で色割当に使う。node --test で検証する。

// 連続（sequential）: 低→高。ColorBrewer 系の代表色を端点+中間で近似。
const SEQUENTIAL = {
  YlOrRd: [
    [255, 255, 178],
    [254, 204, 92],
    [253, 141, 60],
    [240, 59, 32],
    [189, 0, 38],
  ],
  Blues: [
    [239, 243, 255],
    [189, 215, 231],
    [107, 174, 214],
    [49, 130, 189],
    [8, 81, 156],
  ],
  Greens: [
    [237, 248, 233],
    [186, 228, 179],
    [116, 196, 118],
    [49, 163, 84],
    [0, 109, 44],
  ],
  Viridis: [
    [68, 1, 84],
    [59, 82, 139],
    [33, 145, 140],
    [94, 201, 98],
    [253, 231, 37],
  ],
}

// 発散（diverging）: 低端←中点→高端。
const DIVERGING = {
  RdBu: [
    [202, 0, 32],
    [244, 165, 130],
    [247, 247, 247],
    [146, 197, 222],
    [5, 113, 176],
  ],
  RdYlGn: [
    [215, 25, 28],
    [253, 174, 97],
    [255, 255, 191],
    [166, 217, 106],
    [26, 150, 65],
  ],
}

// カテゴリ（qualitative）: 名義データ用。循環使用する。
const CATEGORICAL = {
  Set2: [
    [102, 194, 165],
    [252, 141, 98],
    [141, 160, 203],
    [231, 138, 195],
    [166, 216, 84],
    [255, 217, 47],
    [229, 196, 148],
    [179, 179, 179],
  ],
  Tableau10: [
    [78, 121, 167],
    [242, 142, 43],
    [225, 87, 89],
    [118, 183, 178],
    [89, 161, 79],
    [237, 201, 72],
    [176, 122, 161],
    [255, 157, 167],
    [156, 117, 95],
    [186, 176, 172],
  ],
}

export const COLOR_SCHEMES = {
  numeric: [...Object.keys(SEQUENTIAL), ...Object.keys(DIVERGING)],
  categorical: Object.keys(CATEGORICAL),
}

// スキーム名 → 種別。未知名は sequential 扱い。
export function schemeKind(name) {
  if (name in DIVERGING) return 'diverging'
  if (name in CATEGORICAL) return 'categorical'
  return 'sequential'
}

// スキームのアンカー色配列を返す（未知名は YlOrRd にフォールバック）。
function anchorsOf(name) {
  return SEQUENTIAL[name] ?? DIVERGING[name] ?? CATEGORICAL[name] ?? SEQUENTIAL.YlOrRd
}

// アンカー間を線形補間して t∈[0,1] の色を返す。
function lerpAnchors(anchors, t) {
  if (anchors.length === 1) return anchors[0].slice()
  const clamped = t < 0 ? 0 : t > 1 ? 1 : t
  const pos = clamped * (anchors.length - 1)
  const i = Math.floor(pos)
  if (i >= anchors.length - 1) return anchors[anchors.length - 1].slice()
  const f = pos - i
  const a = anchors[i]
  const b = anchors[i + 1]
  return [
    Math.round(a[0] + (b[0] - a[0]) * f),
    Math.round(a[1] + (b[1] - a[1]) * f),
    Math.round(a[2] + (b[2] - a[2]) * f),
  ]
}

// スキームから n 色をサンプリングして返す（[[r,g,b], …]）。
// 連続/発散: アンカー間を等間隔補間。カテゴリ: アンカーを順に循環。
export function sampleScheme(name, n) {
  const count = Math.max(1, Math.floor(n) || 1)
  const anchors = anchorsOf(name)
  if (schemeKind(name) === 'categorical') {
    return Array.from({ length: count }, (_, i) => anchors[i % anchors.length].slice())
  }
  if (count === 1) return [lerpAnchors(anchors, 0.5)]
  return Array.from({ length: count }, (_, i) => lerpAnchors(anchors, i / (count - 1)))
}
